import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LangContext";
import { pathFor } from "../lib/routes";

const CTA_LABEL = {
  fr: "Demander une session",
  en: "Request a session",
  it: "Richiedi una sessione",
  ru: "Запросить сеанс",
};

const EASE = [0.22, 1, 0.36, 1];

export default function PricingCard({ item, index = 0 }) {
  const { lang } = useLang();
  const isSignature = item.key === "individual";

  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: EASE }}
      whileHover={{ y: -6 }}
      className={`price-card group relative flex flex-col bg-[var(--surface-1)] p-8 md:p-10 ${isSignature ? "price-card--signature" : ""}`}
      data-testid={`pricing-${item.key}`}
    >
      <span className="price-card__sweep" aria-hidden="true" />

      <div className="flex items-center justify-between min-h-[26px]">
        <span className="overline">0{index + 1}</span>
        {isSignature ? (
          <span className="signature-badge" data-testid="pricing-signature-badge">Signature</span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink)]/40 font-display">2026</span>
        )}
      </div>

      <h3 className="font-display text-xl md:text-2xl text-[var(--ink)] mt-6 font-light leading-tight min-h-[2.6em]">
        {item[lang] || item.fr}
      </h3>

      <div className="mt-6 md:mt-8">
        <div className="font-serif-luxe text-[40px] lg:text-[56px] leading-[0.95] tracking-tight text-[var(--ink)] whitespace-nowrap">
          {item[`price_${lang}`] || item.price}
        </div>
        <div className="mt-4 flex items-center gap-2.5">
          <span className="w-1 h-1 rounded-full bg-[var(--gold)] shrink-0" aria-hidden="true" />
          <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-secondary)] font-display">
            {item[`price_unit_${lang}`] || item.price_unit_en || item.price_unit_fr}
          </span>
        </div>
      </div>

      <p className="mt-7 text-[13px] text-[var(--text-secondary)] leading-relaxed font-body">
        {item[`desc_${lang}`] || item.desc_fr}
      </p>

      <Link
        to={pathFor("booking", lang)}
        className="mt-auto pt-9 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-display font-medium text-[var(--ink)]/70 hover:text-[var(--gold)] transition-colors duration-300"
        data-testid={`pricing-cta-${item.key}`}
      >
        <span className="border-b border-[var(--ink)]/15 pb-1 group-hover:border-[var(--gold)]/50 transition-colors duration-300">
          {CTA_LABEL[lang] || CTA_LABEL.en}
        </span>
        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </motion.article>
  );
}
